import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Save, Image, Link2, ExternalLink, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SectionsManager = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [success, setSuccess] = useState({});

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/sections`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSections(data);
      }
    } catch (err) {
      console.error('Failed to fetch sections:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (key, field, value) => {
    setSections(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
    setSuccess(prev => ({ ...prev, [key]: false }));
  };

  const handleSave = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/sections/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(sections[key])
      });
      if (res.ok) {
        setSuccess(prev => ({ ...prev, [key]: true }));
        setTimeout(() => setSuccess(prev => ({ ...prev, [key]: false })), 3000);
      }
    } catch (err) {
      console.error('Failed to save section:', err);
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="sections-title">Landing Page Sections</h1>
              <p className="text-gray-500 text-sm">Manage images and CTA buttons for each homepage section</p>
            </div>
          </div>
        </div>

        {/* Section Cards */}
        <div className="space-y-6">
          {Object.entries(sections).map(([key, section]) => (
            <Card key={key} className="bg-gray-900 border-gray-800" data-testid={`section-card-${key}`}>
              <CardHeader className="pb-4">
                <CardTitle className="text-white text-lg flex items-center justify-between">
                  {section.title}
                  {success[key] && (
                    <span className="flex items-center gap-1 text-emerald-400 text-sm font-normal">
                      <CheckCircle className="w-4 h-4" /> Saved
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Image Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label className="text-gray-300 flex items-center gap-2">
                      <Image className="w-4 h-4" /> Image URL
                    </Label>
                    <Input
                      value={section.image || ''}
                      onChange={(e) => handleUpdate(key, 'image', e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="bg-gray-800 border-gray-700 text-white"
                      data-testid={`section-image-${key}`}
                    />

                    {/* Drag & drop upload */}
                    <div className="relative">
                      <input
                        type="file"
                        id={`upload-${key}`}
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onloadend = () => handleUpdate(key, 'image', reader.result);
                          reader.readAsDataURL(file);
                        }}
                      />
                      <label
                        htmlFor={`upload-${key}`}
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-amber-400', 'bg-amber-500/10'); }}
                        onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-amber-400', 'bg-amber-500/10'); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('border-amber-400', 'bg-amber-500/10');
                          const file = e.dataTransfer?.files?.[0];
                          if (!file || !file.type.startsWith('image/')) return;
                          const reader = new FileReader();
                          reader.onloadend = () => handleUpdate(key, 'image', reader.result);
                          reader.readAsDataURL(file);
                        }}
                        className="flex items-center justify-center w-full h-20 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:border-amber-500 hover:bg-gray-800/50 transition-colors"
                      >
                        <p className="text-gray-500 text-sm">Click or drag & drop image here</p>
                      </label>
                    </div>
                  </div>

                  {/* Preview */}
                  <div>
                    <Label className="text-gray-400 text-sm mb-2 block">Preview</Label>
                    <div className="aspect-video rounded-lg overflow-hidden border border-gray-700 bg-gray-800">
                      {section.image ? (
                        <img
                          src={section.image}
                          alt={section.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-10 h-10 text-gray-600" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* CTA Section */}
                <div className="border-t border-gray-800 pt-4">
                  <Label className="text-gray-300 flex items-center gap-2 mb-3">
                    <Link2 className="w-4 h-4" /> CTA Button
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-500 text-xs mb-1 block">Button Text</Label>
                      <Input
                        value={section.cta_text || ''}
                        onChange={(e) => handleUpdate(key, 'cta_text', e.target.value)}
                        placeholder="Click here"
                        className="bg-gray-800 border-gray-700 text-white"
                        data-testid={`section-cta-text-${key}`}
                      />
                    </div>
                    <div>
                      <Label className="text-gray-500 text-xs mb-1 block">Button Link</Label>
                      <Input
                        value={section.cta_link || ''}
                        onChange={(e) => handleUpdate(key, 'cta_link', e.target.value)}
                        placeholder="/page or https://..."
                        className="bg-gray-800 border-gray-700 text-white"
                        data-testid={`section-cta-link-${key}`}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <Switch
                      checked={section.cta_external || false}
                      onCheckedChange={(val) => handleUpdate(key, 'cta_external', val)}
                      data-testid={`section-cta-external-${key}`}
                    />
                    <Label className="text-gray-400 text-sm flex items-center gap-1">
                      <ExternalLink className="w-3.5 h-3.5" /> Open in new tab (external link)
                    </Label>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={() => handleSave(key)}
                    disabled={saving[key]}
                    className="bg-amber-600 hover:bg-amber-500"
                    data-testid={`section-save-${key}`}
                  >
                    {saving[key] ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {saving[key] ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SectionsManager;
